import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  ViewChild
} from '@angular/core';

import { IonSearchbar, PopoverController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { isEmpty, isNull } from 'lodash-es';
import { map, Observable, tap } from 'rxjs';

import { PrimarySubscriptionPlanAccessAliasEnum } from '../../../enums/monetization';
import * as fromPropertyProfileActions from '../../../store/actions/property-profile.actions';
import * as fromPropertyProfileSelectors from '../../../store/selectors/property-profile.selectors';
import { MonetizationService } from '../../services/monetization.service';
import { SearchMenuComponent } from '../search-menu/search-menu.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-map-search',
  standalone: false,
  templateUrl: './map-search.component.html'
})
export class MapSearchComponent implements AfterViewInit {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private monetizationService = inject(MonetizationService);
  private popoverController = inject(PopoverController);
  private store = inject(Store);

  @ViewChild(IonSearchbar, { read: IonSearchbar, static: false })
  searchBarInput?: IonSearchbar;

  @Input() hasPropertySearchMenu = false;

  searchText$: Observable<string>;

  searchBarExpanded = false;
  showMoreButton = true;

  constructor() {
    this.searchText$ = this.store.pipe(
      select(fromPropertyProfileSelectors.selectPropertySearchText()),
      map((text) => (isNull(text) ? '' : text)),
      tap((text) => {
        this.showMoreButton = isEmpty(text);
      })
    );
  }

  async ngAfterViewInit() {
    if (this.searchBarInput) {
      const searchInput = await this.searchBarInput.getInputElement();
      const placesAutocomplete = new google.maps.places.Autocomplete(searchInput, {
        componentRestrictions: {
          country: 'USA'
        },
        fields: ['formatted_address'],
        types: ['address']
      });

      placesAutocomplete.addListener('place_changed', async () => {
        const selectedPlace = placesAutocomplete.getPlace();
        if (selectedPlace) {
          const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
          if (hasAccess) {
            if (selectedPlace.formatted_address) {
              this.store.dispatch(
                fromPropertyProfileActions.geocodeAddressLocation({
                  payload: { address: selectedPlace.formatted_address }
                })
              );
            } else if (selectedPlace.name) {
              this.store.dispatch(
                fromPropertyProfileActions.geocodeAddressLocation({ payload: { address: selectedPlace.name } })
              );
            } else {
              this.store.dispatch(
                fromPropertyProfileActions.geocodeLocationFailure({
                  payload: {
                    error: new Error(
                      `No location data could be found for ${selectedPlace.name ? '"' + selectedPlace.name + '"' : ' your search'}. Please make sure you typed/selected a valid address.`
                    )
                  }
                })
              );
            }
          }
        }
      });
    }
  }

  async searchBarClear() {
    if (this.searchBarInput) {
      const input = await this.searchBarInput.getInputElement();
      input.blur();
      this.store.dispatch(fromPropertyProfileActions.resetSearch());
    }
  }

  async toggleSearchBarMenu() {
    const customEvent = new MouseEvent('click');
    Object.defineProperty(customEvent, 'target', {
      enumerable: true,
      value: await this.searchBarInput?.getInputElement()
    });
    const popover = await this.popoverController.create({
      alignment: 'end',
      arrow: false,
      component: SearchMenuComponent,
      cssClass: 'search-menu',
      event: customEvent,
      showBackdrop: false,
      side: 'bottom',
      size: 'cover'
    });

    this.searchBarExpanded = true;
    await popover.present();
    this.changeDetectorRef.detectChanges();
    await popover.onDidDismiss();
    this.searchBarExpanded = false;
    this.changeDetectorRef.detectChanges();
  }

  searchBarChange(event: CustomEvent) {
    this.showMoreButton = isEmpty(event.detail.value);
  }
}
